import { Link, useLocation } from "react-router-dom";
import {
  BookOpenText,
  ExternalLink,
  LogOut,
  PanelLeft,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { getRouteMeta } from "@/components/layout/navigation";
import { useAuthStore, type User } from "@/stores/auth";

interface HeaderProps {
  user: User | null;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const API_BASE = (import.meta.env.VITE_API_BASE || "").trim();
const docsHref = API_BASE ? `${API_BASE.replace(/\/+$/, "")}/openapi` : "/openapi";

function formatRoleLabel(role?: string | null): string {
  switch ((role ?? "").trim().toLowerCase()) {
    case "admin":
      return "Administrator";
    case "moderator":
      return "Moderator";
    case "user":
      return "User";
    default:
      return "Administrator";
  }
}

export default function Header({
  user,
  sidebarOpen,
  setSidebarOpen,
}: HeaderProps) {
  const location = useLocation();
  const { logout } = useAuthStore();
  const routeMeta = getRouteMeta(location.pathname);

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="sticky top-0 z-20 border-b border-border/80 bg-background/88 backdrop-blur-xl">
      <div className="flex h-16 items-center justify-between gap-4 px-4 lg:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <PanelLeft className="h-5 w-5" />
          </Button>

          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
              Admin workspace
            </p>
            <div className="flex min-w-0 items-center gap-3">
              <h1 className="truncate text-base font-semibold">
                {routeMeta.title}
              </h1>
              <p className="hidden truncate text-sm text-muted-foreground xl:block">
                {routeMeta.description}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
            <Link to="/assets/clothes">Public catalog</Link>
          </Button>

          <Button asChild variant="ghost" size="sm" className="hidden lg:inline-flex">
            <a href={docsHref} target="_blank" rel="noreferrer">
              <BookOpenText className="mr-2 h-4 w-4" />
              OpenAPI
            </a>
          </Button>

          <div className="hidden min-w-0 items-center gap-3 rounded-xl border border-border/80 bg-card/80 px-3 py-2 sm:flex">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/12 text-sm font-semibold text-primary">
              {(user?.username || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {user?.username || "Admin"}
              </p>
              <p className="truncate text-xs text-muted-foreground capitalize">
                {formatRoleLabel(user?.role)}
              </p>
            </div>
          </div>

          <Button asChild variant="ghost" size="icon" className="hidden sm:inline-flex">
            <a
              href={docsHref}
              target="_blank"
              rel="noreferrer"
              aria-label="Open API documentation"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>

          <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Sign out">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
